import React from 'react'
import { Stack, Typography } from '@mui/material'
import {
  AlertContainerComponent,
  AlertContainerComponentProps,
} from '@app/components/ui/containers/AlertContainer'
import ReviewIcon from '@app/components/ui/icons/ReviewIcon'
import { FeedbackContent } from '@app/components/feedback/FeedbackContent'

export type ItemFeedbackProps = {
  correct?: boolean
  correctAnswer?: React.ReactNode
  raw?: number
  possible?: number
  forceMultiscore?: boolean
  multiScoreFormatter?: (raw: number, possible: number) => string
  correctAnswerFormatter?: (correctAnswer: React.ReactNode) => React.ReactNode
  size?: AlertContainerComponentProps['size']
  showScoreLabel?: boolean
  showIcon?: boolean
  className?: string
  accessibilityAttr?: Record<string, string>
  generalFeedback?: React.ReactNode
}

const defaultMultiScoreFormatter = (raw: number, possible: number): string => {
  return `${raw} of ${possible} Answers Correct`
}

const defaultCorrectAnswerFormatter = (correctAnswer: React.ReactNode) => {
  return (
    <Typography variant='body2' component="span">
      Correct Answer: {correctAnswer}
    </Typography>
  )
}

export const ItemFeedback: React.FC<ItemFeedbackProps> = ({
  correct,
  raw,
  possible,
  forceMultiscore = false,
  multiScoreFormatter = defaultMultiScoreFormatter,
  correctAnswerFormatter = defaultCorrectAnswerFormatter,
  className,
  correctAnswer,
  size,
  showScoreLabel = true,
  showIcon = true,
  accessibilityAttr = {},
  generalFeedback,
}) => {
  const isMultiScore = forceMultiscore || possible > 1

  return (
    <AlertContainerComponent
      severity={correct ? 'success' : 'warning'}
      variant='outlined'
      size={size}
      className={className}
      icon={showIcon && <ReviewIcon correct={correct} />}
      {...accessibilityAttr}
      data-automation-id="item-feedback-alert-container"
    >
      <Stack gap={1} data-automation-id="item-feedback-stack">
        {showScoreLabel && (
          <Typography variant='body2' color="inherit" component="span" data-automation-id="item-correctness" sx={{ fontWeight: 700 }}>
            {isMultiScore
              ? multiScoreFormatter(raw, possible)
              : correct ? 'Correct' : 'Incorrect'
            }
          </Typography>
        )}
        {!correct && correctAnswer && correctAnswerFormatter(correctAnswer)}
        {generalFeedback && (
          <FeedbackContent
            variant="body2"
            component="div"
            data-automation-id="item-general-feedback"
          >
            {generalFeedback}
          </FeedbackContent>
        )}
      </Stack>
    </AlertContainerComponent>
  )
}
